import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import ChampIdentifiant from './ChampIdentifiant';

/**
 * Saisie d'une installation photovoltaïque.
 *
 * Une remarque de fond sur la marque : pour le PV, ce qui compte est la marque du
 * PANNEAU, pas celle de l'installateur ni d'un « ensemble ». Le champ générique
 * « Marque » a donc disparu au profit de `panneau_marque` / `panneau_modele`, qui
 * remontent ensuite comme marque et modèle de l'actif.
 *
 * Le type d'onduleur n'est pas décoratif : un micro-onduleur par panneau ne se pilote
 * pas comme un onduleur centralisé.
 *
 * CE QUI IDENTIFIE UNE INSTALLATION, DEPUIS LE 27/08/2026 : L'ID CENTRAL
 *
 * C'était le numéro de série de l'onduleur. Précision de David : l'ID central est
 * l'identifiant unique attribué à l'installation dans l'application Hoymiles S-Miles
 * Cloud. Le numéro d'onduleur, lui, CHANGE quand on remplace l'onduleur — un SAV, et
 * l'installation perd son historique de télémétrie, donc sa preuve d'effacement, donc
 * de quoi facturer. Le numéro de série reste saisi et affiché : il sert au dépannage,
 * plus à l'identité.
 */
export default function BlocPV({ v, upE, t }) {
  const champ = (cle, secours, valeur, options = {}) => (
    <div>
      <Label className="text-[11px] text-neutral-500">
        {t(cle, secours)} {options.requis ? '*' : t('lbl_optional', '(optionnel)')}
      </Label>
      <Input
        className="h-11 text-[14px]"
        type={options.type || 'text'}
        min={options.min}
        step={options.step}
        spellCheck={options.type ? undefined : 'true'}
        value={v[valeur]}
        onChange={(e) => upE('PV', valeur, e.target.value)}
        data-testid={`pv-${valeur.replace(/_/g, '-')}`}
      />
    </div>
  );

  const ONDULEURS = [
    { cle: 'microonduleur', titre: 'lbl_onduleur_micro', secours: 'Micro-onduleurs',
      desc: 'lbl_onduleur_micro_desc', descSecours: '1 micro-onduleur par panneau (Hoymiles…)' },
    { cle: 'centralise', titre: 'lbl_onduleur_centralise', secours: 'Onduleur centralisé',
      desc: 'lbl_onduleur_centralise_desc', descSecours: '1 seul onduleur pour tous les panneaux' },
  ];

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        {champ('lbl_puissance_kwc', 'Puissance totale (kWc)', 'puissance_kw',
          { type: 'number', step: '0.1', requis: true })}
        {champ('lbl_nombre_panneaux', 'Nombre de panneaux', 'nombre_panneaux',
          { type: 'number', min: '1', requis: true })}
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50/40 p-3 space-y-2">
        <p className="text-[11px] font-medium text-amber-700">
          {t('lbl_panneaux_section', 'Panneaux photovoltaïques')}
        </p>
        <div className="grid grid-cols-2 gap-3">
          {champ('lbl_panneau_marque', 'Marque panneau', 'panneau_marque', { requis: true })}
          {champ('lbl_panneau_modele', 'Modèle panneau', 'panneau_modele', { requis: true })}
        </div>
        {champ('lbl_puissance_panneau', 'Puissance / panneau (Wc)', 'puissance_panneau_wc',
          { type: 'number', min: '1', requis: true })}
      </div>

      <div className="space-y-2">
        <Label className="text-[11px] text-neutral-500">{t('lbl_type_onduleur', "Type d'onduleur")} *</Label>
        <div className="grid grid-cols-2 gap-2">
          {ONDULEURS.map((o) => (
            <button
              key={o.cle}
              type="button"
              onClick={() => upE('PV', 'type_onduleur', o.cle)}
              className={`p-3 rounded-lg border text-left transition-colors ${
                v.type_onduleur === o.cle
                  ? 'border-amber-400 bg-amber-50 text-neutral-900'
                  : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'
              }`}
              data-testid={`pv-onduleur-${o.cle}`}
            >
              <p className="text-[12.5px] font-medium">{t(o.titre, o.secours)}</p>
              <p className="text-[10.5px] text-neutral-500 mt-0.5">{t(o.desc, o.descSecours)}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {champ('lbl_onduleur_marque', 'Marque onduleur', 'onduleur_marque')}
        {champ('lbl_onduleur_modele', 'Modèle onduleur', 'onduleur_modele')}
      </div>
      {/* C'est l'ID CENTRAL qui remonte comme identifiant de l'actif, donc c'est lui
          qu'on confronte au matériel réel. Il ne change pas quand l'onduleur est
          remplacé, contrairement au numéro de série ci-dessous.
          Ce champ n'a plus d'aide depuis le 26/10 (lot L3, Loom de David du 13/10) :
          l'explication sur S-Miles Cloud décrivait notre identification du site, pas
          un geste du poseur. Le libellé suffit. */}
      <ChampIdentifiant
        type="PV" v={v} upE={upE} t={t} cle="onduleur_id"
        libelle="ID central (Hoymiles S-Miles)"
      />
      {/* PLUS OBLIGATOIRE depuis le 31/08 — « il ne faut pas que les ID central
          Hoymiles ou le numéro de série soit bloquants ». L'ID central au-dessus
          suffit à identifier le site, et il ne change pas si l'onduleur est remplacé.
          Exiger les deux bloquait le poseur sur une étiquette souvent illisible. */}
      {champ('lbl_onduleur_serial', 'Numéro de série onduleur', 'onduleur_serial',
        { requis: false })}
      <div>
        <Label className="text-[11px] text-neutral-500">{t('lbl_date_installation', "Date d'installation")} *</Label>
        <Input
          className="h-11 text-[14px]"
          type="date"
          value={v.date_installation}
          onChange={(e) => upE('PV', 'date_installation', e.target.value)}
          data-testid="pv-date"
        />
      </div>
    </>
  );
}
